// =============================================================================
// Inventory Management — the chrome every inventory screen shares.
//
// The module is read as a spreadsheet replacement, so the shared styling is a dense table and a filter
// bar rather than cards: operations compare 1,900 rows, and whitespace that flatters a dashboard makes
// that job harder. Status colour is the one decoration, because a pallet's state is what the eye scans
// for first.
// =============================================================================

import React from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { formatCalendarDate, formatDate } from '/imports/utils';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { useFindOne, useUser } from '/imports/hooks';
import { Accounts } from '/imports/api/accounts';
import {
  InventoryAllocationStatus, InventoryDeliveryStatus, InventoryPalletStatus,
  inventoryAllocationStatusLabel, inventoryCanAccess, inventoryCanEdit, inventoryCanEditFinance,
  inventoryDeliveryStatusLabel, inventoryMoney, inventoryPalletStatusLabel,
} from '/imports/api/inventory';

export const InventoryPageStyled = styled(Page)`
  .page-body { height: 0; }

  .inv-bar {
    display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center;
    padding: 0.6rem 0; border-bottom: 1px solid ${css.grey7}; margin-bottom: 0.75rem;
  }
  .inv-bar .rich-select, .inv-bar select { height: 2.2rem; min-height: 2.2rem; font-size: 0.85rem; padding: 0 0.5rem; width: auto; }
  .inv-bar input { height: 2.2rem; min-height: 2.2rem; font-size: 0.85rem; }
  .inv-bar .search { width: 17rem; }
  .inv-bar .spacer { margin-left: auto; }
  .inv-bar .meta { color: ${css.grey4}; font-size: 0.8rem; white-space: nowrap; }

  table.inv { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
  table.inv th, table.inv td { padding: 0.35rem 0.5rem; text-align: left; border-bottom: 1px solid ${css.grey7}; white-space: nowrap; }
  table.inv th { color: ${css.grey3}; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.03em; position: sticky; top: 0; background: ${css.white}; z-index: 1; }
  table.inv th.num, table.inv td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.inv tbody tr:hover { background: ${css.grey9}; }
  table.inv tr.clickable { cursor: pointer; }
  table.inv tfoot td { font-weight: 600; border-top: 2px solid ${css.grey6}; border-bottom: none; }
  .scroll-x { overflow-x: auto; }

  .muted { color: ${css.grey4}; }
  .empty { padding: 1.5rem 0; color: ${css.grey4}; }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font-size: inherit; }
  .neg { color: ${css.red5}; }
  .pos { color: ${css.green}; }

  .tiles { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1rem; }
  .tile { flex: 1 1 10rem; border: 1px solid ${css.grey7}; border-radius: ${css.borderRadius}; padding: 0.6rem 0.8rem; }
  .tile .k { color: ${css.grey4}; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; }
  .tile .v { font-size: 1.35rem; font-weight: 600; font-variant-numeric: tabular-nums; }
  .tile .sub { color: ${css.grey4}; font-size: 0.75rem; }

  .panels { display: grid; grid-template-columns: repeat(auto-fit, minmax(22rem, 1fr)); gap: 1rem; }
  .panel-card { border: 1px solid ${css.grey7}; border-radius: ${css.borderRadius}; padding: 0.75rem 0.9rem; min-width: 0; }
  .panel-card > h2 { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em; color: ${css.grey3}; margin: 0 0 0.5rem; }
  .panel-card .scroll-x { max-width: 100%; }

  .facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr)); gap: 0.4rem 1rem; }
  .fact .k { color: ${css.grey4}; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; }
  .fact .v { font-size: 0.9rem; font-variant-numeric: tabular-nums; }

  .chip {
    display: inline-block; padding: 0.1rem 0.45rem; border-radius: 999px;
    font-size: 0.72rem; font-weight: 600; white-space: nowrap;
  }
  .chip.grey { background: ${css.grey8}; color: ${css.grey2}; }
  .chip.blue { background: ${css.primaryColorTransparent}; color: ${css.primaryColor}; }
  .chip.green { background: rgba(16, 158, 103, 0.14); color: ${css.green}; }
  .chip.amber { background: rgba(250, 185, 1, 0.18); color: #8a6300; }
  .chip.red { background: ${css.red7}; color: ${css.red4}; }

  .note { color: ${css.grey4}; font-size: 0.78rem; margin: 0.25rem 0 0.75rem; }
`;

/** What the signed-in account may do in this module. */
export const useInventoryAccess = () => {
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  return {
    user,
    canAccess: inventoryCanAccess(user, account),
    canEdit: inventoryCanEdit(user, account),
    canEditFinance: inventoryCanEditFinance(user, account),
    isSuperUser: !!user?.isSuperUser,
  };
};

type ChipTone = 'grey' | 'blue' | 'green' | 'amber' | 'red';

/** Colour for a pallet status: what has left the building, and what needs attention. */
const palletTone = (status?: InventoryPalletStatus): ChipTone => {
  switch (status) {
    case 'onOrder': return 'grey';
    case 'inStock': case 'reserved': case 'allocated': return 'blue';
    case 'stagedForShipment': case 'inTransit': return 'amber';
    case 'delivered': case 'installed': return 'green';
    default: return 'red';
  }
};

const deliveryTone = (status?: InventoryDeliveryStatus): ChipTone => {
  switch (status) {
    case 'requested': case 'scheduled': return 'blue';
    case 'staged': case 'inTransit': return 'amber';
    case 'delivered': case 'installed': return 'green';
    case 'exception': return 'red';
    default: return 'grey';
  }
};

const allocationTone = (status?: InventoryAllocationStatus): ChipTone => {
  switch (status) {
    case 'complete': return 'green';
    case 'partial': return 'amber';
    default: return 'red';
  }
};

export const PalletStatusChip = ({ status }: { status?: InventoryPalletStatus }) => (
  <span className={`chip ${palletTone(status)}`}>{inventoryPalletStatusLabel(status)}</span>
);

export const DeliveryStatusChip = ({ status }: { status?: InventoryDeliveryStatus }) => (
  <span className={`chip ${deliveryTone(status)}`}>{inventoryDeliveryStatusLabel(status)}</span>
);

export const AllocationChip = ({ status }: { status?: InventoryAllocationStatus }) => (
  <span className={`chip ${allocationTone(status)}`}>{inventoryAllocationStatusLabel(status)}</span>
);

/** A labelled figure. */
export const Tile = (props: { label: string; value: React.ReactNode; sub?: React.ReactNode }) => (
  <div className="tile">
    <div className="k">{props.label}</div>
    <div className="v">{props.value}</div>
    {props.sub != null && <div className="sub">{props.sub}</div>}
  </div>
);

/** A labelled value inside a facts grid. */
export const Fact = (props: { label: string; value: React.ReactNode }) => (
  <div className="fact">
    <div className="k">{props.label}</div>
    <div className="v">{props.value ?? <span className="muted">—</span>}</div>
  </div>
);

/** An em dash where a value is missing, so an empty cell never reads as a zero. */
export const orDash = (value: React.ReactNode | undefined | null): React.ReactNode =>
  (value === undefined || value === null || value === '' ? <span className="muted">—</span> : value);

/** Counts, with the separators the figures are read with. */
export const count = (n?: number): React.ReactNode => (n == null ? orDash(undefined) : n.toLocaleString());

/** Money, whole dollars unless the ledger needs cents. */
export const money = (n?: number, cents?: boolean): React.ReactNode => (n == null ? orDash(undefined) : inventoryMoney(n, cents));

/** A balance, coloured only when it is a shortfall; a surplus is normal and shown plainly. */
export const balance = (n?: number): React.ReactNode => {
  if (n == null) return orDash(undefined);
  if (n > 0) return <span className="neg">{n.toLocaleString()}</span>;
  return <span>{n.toLocaleString()}</span>;
};

/**
 * A calendar date: a delivery day, a receipt, a guaranteed delivery date. Rendered in UTC, because these
 * are days rather than instants and reading them in the viewer's zone shifts them west of Greenwich.
 */
export const day = (value?: Date | string): React.ReactNode => {
  if (!value) return orDash(undefined);
  const formatted = formatCalendarDate(value);
  return formatted || orDash(undefined);
};

/** A real moment — when somebody did something — which does belong in the viewer's own zone. */
export const stamp = (value?: Date | string): React.ReactNode => {
  if (!value) return orDash(undefined);
  const formatted = formatDate(value instanceof Date ? value : new Date(value));
  return formatted || orDash(undefined);
};

/** The standard empty-state line. */
export const EmptyRow = ({ colSpan, label }: { colSpan: number; label?: string }) => (
  <tr><td className="empty" colSpan={colSpan}>{label || strings.NoResults()}</td></tr>
);
